import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../services/api';
import { useAuth } from '../../auth/AuthContext';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ChevronLeft, ChevronRight, Info, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_COLORS = {
  CREATE: { bg: '#f0fdf4', color: '#14532d', border: '#bbf7d0' },
  UPDATE: { bg: '#eff6ff', color: '#1e3a8a', border: '#bfdbfe' },
  DELETE: { bg: '#fef2f2', color: '#7f1d1d', border: '#fecaca' },
  LOGIN: { bg: '#f8fafc', color: '#1e293b', border: '#e2e8f0' },
  LOGOUT: { bg: '#f8fafc', color: '#1e293b', border: '#e2e8f0' },
  PRICE_CHANGE: { bg: '#fffbeb', color: '#78350f', border: '#fde68a' },
  PASSWORD_RESET: { bg: '#fdf4ff', color: '#581c87', border: '#e9d5ff' },
};

const ActionBadge = ({ action }) => {
  const s = ACTION_COLORS[action] || ACTION_COLORS.UPDATE;
  return <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>{action}</span>;
};

export default function AuditLogAdmin() {
  const { isAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ entity_type: '', action: '' });
  const [detail, setDetail] = useState(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);
  const [purging, setPurging] = useState(false);
  const PAGE_SIZE = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.action) params.action = filters.action;
      const { data } = await adminAPI.getAuditLog(params);
      setLogs(data.items || []); setTotal(data.total || 0);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' +
           d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handlePurge = async () => {
    setPurging(true);
    try {
      const { data } = await adminAPI.purgeAuditLog();
      toast.success(`Purged ${data.deleted_count} entries older than ${data.retention_days} days`);
      setShowPurgeConfirm(false);
      setPage(1);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to purge audit log'); }
    finally { setPurging(false); }
  };

  return (
    <div className="space-y-5" data-testid="audit-log-table">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'var(--font-display)', color: 'hsl(var(--c4p-navy-900))' }}>Audit Log</h2>
          <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-fg))' }}>Immutable log of all system mutations. {total} total entries.</p>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" className="gap-1.5" style={{ color: '#b91c1c', borderColor: '#fecaca' }}
            onClick={() => setShowPurgeConfirm(true)} data-testid="purge-audit-log-button">
            <Trash2 size={13} /> Purge Old Logs
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select value={filters.entity_type || 'all'} onValueChange={v => { setFilters(f => ({ ...f, entity_type: v === 'all' ? '' : v })); setPage(1); }}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All entity types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {['AUTH', 'USER', 'CUSTOMER', 'QUOTE', 'PRICING', 'EXTRA', 'CURRENCY', 'SETTING'].map(t =>
              <SelectItem key={t} value={t}>{t}</SelectItem>
            )}
          </SelectContent>
        </Select>
        <Select value={filters.action || 'all'} onValueChange={v => { setFilters(f => ({ ...f, action: v === 'all' ? '' : v })); setPage(1); }}>
          <SelectTrigger className="w-40"><SelectValue placeholder="All actions" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All actions</SelectItem>
            {['CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'PRICE_CHANGE', 'PASSWORD_RESET'].map(a =>
              <SelectItem key={a} value={a}>{a}</SelectItem>
            )}
          </SelectContent>
        </Select>
        {(filters.entity_type || filters.action) &&
          <Button variant="ghost" size="sm" onClick={() => { setFilters({ entity_type: '', action: '' }); setPage(1); }}>Clear</Button>
        }
      </div>

      <div className="rounded-xl border bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'hsl(var(--muted))' }}>
              <TableHead>Time</TableHead>
              <TableHead>User</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Entity</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8 w-full" /></TableCell></TableRow>
            )) : logs.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8" style={{ color: 'hsl(var(--muted-fg))' }}>No audit log entries found.</TableCell></TableRow>
            ) : logs.map(l => (
              <TableRow key={l.id} className="data-table-row">
                <TableCell className="font-mono-c4p text-xs whitespace-nowrap">{fmtDate(l.timestamp)}</TableCell>
                <TableCell className="text-sm">{l.user_email}</TableCell>
                <TableCell><ActionBadge action={l.action} /></TableCell>
                <TableCell className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>{l.entity_type}</TableCell>
                <TableCell className="text-sm max-w-[280px] truncate">{l.summary}</TableCell>
                <TableCell>
                  {(l.before || l.after) && (
                    <Button variant="ghost" size="sm" onClick={() => setDetail(l)}><Info size={13} /></Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span style={{ color: 'hsl(var(--muted-fg))' }}>Page {page} of {totalPages} ({total} entries)</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Audit Detail - {detail?.summary}</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            {detail?.before && (
              <div><p className="font-semibold mb-1">Before:</p>
                <pre className="rounded-lg p-3 text-xs overflow-x-auto" style={{ background: 'hsl(var(--muted))' }}>{JSON.stringify(detail.before, null, 2)}</pre></div>
            )}
            {detail?.after && (
              <div><p className="font-semibold mb-1">After:</p>
                <pre className="rounded-lg p-3 text-xs overflow-x-auto" style={{ background: 'hsl(var(--muted))' }}>{JSON.stringify(detail.after, null, 2)}</pre></div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Purge confirmation dialog */}
      <Dialog open={showPurgeConfirm} onOpenChange={setShowPurgeConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Purge Old Audit Logs</DialogTitle></DialogHeader>
          <p className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>
            This permanently deletes audit log entries older than the retention period configured in
            Settings ("Audit Log Retention (days)"). This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPurgeConfirm(false)}>Cancel</Button>
            <Button onClick={handlePurge} disabled={purging} style={{ background: '#b91c1c', color: 'white' }}
              className="gap-1.5" data-testid="confirm-purge-audit-log-button">
              {purging ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Purge Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
